/**
 * One dictation, from the mic click to the last word: microphone → AudioWorklet
 * → transcription socket, with the transcript coming back as the user speaks.
 * React-free so every way it can end is testable; useDictation adapts it.
 */

import { openTranscriptionSocket, type TranscriptionMessage, type TranscriptionSocket } from "@/api";
import { type AudioCapture, connectCapture, requestMicrophone } from "./audioCapture";
import { closeErrorKind, type DictationError, micErrorKind, startErrorKind } from "./errors";
import { applyTranscriptMessage, EMPTY_TRANSCRIPT, type Transcript, transcriptText } from "./transcript";

export type DictationStatus = "idle" | "starting" | "recording" | "finishing";

/** Stop by itself after this much silence (input level below SILENCE_LEVEL). */
export const SILENCE_STOP_MS = 60_000;
const SILENCE_LEVEL = 0.02;
const LEVEL_POLL_MS = 100;
/** After Stop the final normally lands in about half a second; don't wait longer than this. */
export const FINISH_TIMEOUT_MS = 5000;

export interface DictationCallbacks {
  onStatus: (status: DictationStatus) => void;
  /** Everything dictated so far (not a delta). */
  onText: (text: string) => void;
  onError: (error: DictationError) => void;
}

export interface DictationSession {
  /** Stop listening. The last words still arrive, then it ends. */
  stop: () => void;
  /** End now: no more text or errors; the status goes to idle. */
  cancel: () => void;
}

class Dictation implements DictationSession {
  private readonly context: AudioContext;
  private stream: MediaStream | null = null;
  private socket: TranscriptionSocket | null = null;
  private capture: AudioCapture | null = null;
  private transcript: Transcript = EMPTY_TRANSCRIPT;
  private status: DictationStatus = "starting";
  private ended = false;
  /** A final arrived while recording: the model finalizes segment by segment, so the one after Stop may not be the last. */
  private segmented = false;
  private reported = false;
  private silentSince: number | null = null;
  private levelTimer: ReturnType<typeof setInterval> | undefined;
  private finishTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly callbacks: DictationCallbacks) {
    // Created inside the click (a user gesture), so Safari doesn't start it suspended.
    this.context = new AudioContext();
  }

  async begin(): Promise<void> {
    this.setStatus("starting");
    try {
      this.stream = await requestMicrophone();
      if (this.ended) {
        this.release();
        return;
      }
      for (const track of this.stream.getTracks()) track.onended = this.micLost;
      const socket = await openTranscriptionSocket({ onMessage: this.onMessage, onClose: this.onClose });
      this.socket = socket;
      if (this.ended) {
        socket.close();
        return;
      }
      this.capture = await connectCapture(this.context, this.stream, (chunk) => socket.sendAudio(chunk));
      if (this.ended) {
        this.release();
        return;
      }
      this.setStatus("recording");
      this.levelTimer = setInterval(this.checkSilence, LEVEL_POLL_MS);
    } catch (error) {
      this.fail(this.stream ? startErrorKind(error) : micErrorKind(error));
    }
  }

  stop(): void {
    if (this.ended) return;
    if (this.status !== "recording") {
      this.end();
      return;
    }
    this.release();
    this.socket?.stop();
    this.setStatus("finishing");
    this.finishTimer = setTimeout(() => this.end(), FINISH_TIMEOUT_MS);
  }

  cancel(): void {
    this.end();
  }

  private readonly onMessage = (message: TranscriptionMessage): void => {
    if (this.ended) return;
    if (message.type === "error") {
      // Stop listening but keep what's transcribed: the message says voice input stopped.
      this.report("failed");
      if (this.status === "recording") this.stop();
      return;
    }
    this.transcript = applyTranscriptMessage(this.transcript, message);
    this.callbacks.onText(transcriptText(this.transcript));
    if (message.type !== "final") return;
    if (this.status === "recording") this.segmented = true;
    else if (this.status === "finishing" && !this.segmented) this.end();
  };

  private readonly onClose = (code: number): void => {
    if (this.status === "finishing") this.end();
    else this.fail(closeErrorKind(code));
  };

  // By the clock, not by ticks: a background tab runs timers about once a second.
  private readonly checkSilence = (): void => {
    if ((this.capture?.level() ?? 0) >= SILENCE_LEVEL) {
      this.silentSince = null;
      return;
    }
    this.silentSince ??= Date.now();
    if (Date.now() - this.silentSince >= SILENCE_STOP_MS) this.stop();
  };

  /** The mic went away (unplugged, permission revoked): stop, keeping what's transcribed. */
  private readonly micLost = (): void => {
    this.report("noMicrophone");
    this.stop();
  };

  private fail(error: DictationError): void {
    if (this.ended) return;
    this.report(error);
    this.end();
  }

  private report(error: DictationError): void {
    if (this.reported) return;
    this.reported = true;
    this.callbacks.onError(error);
  }

  /** Let go of the mic and the audio graph (safe to repeat). */
  private release(): void {
    clearInterval(this.levelTimer);
    this.capture?.stop();
    for (const track of this.stream?.getTracks() ?? []) track.stop();
  }

  private end(): void {
    if (this.ended) return;
    this.ended = true;
    clearTimeout(this.finishTimer);
    this.release();
    void this.context.close();
    this.socket?.close();
    this.setStatus("idle");
  }

  private setStatus(status: DictationStatus): void {
    this.status = status;
    this.callbacks.onStatus(status);
  }
}

/** Start dictating. Call it from the click handler: the AudioContext has to be created in the gesture. */
export function startDictationSession(callbacks: DictationCallbacks): DictationSession {
  const session = new Dictation(callbacks);
  void session.begin();
  return session;
}
